import { Dispatch, FC, SetStateAction, createContext, useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { setMapMode } from 'store/mapSlice';
import { RootState } from 'store/rootReducer';
import { clearUtkast } from 'store/utkastSlice';

import { useAppDispatch, useAppSelector } from 'hooks/defaultStoreHooks';
import { useDrawerMobileTemplate } from 'hooks/useDrawerMobileTemplate';
import { useInitialFocusOnExtent } from 'hooks/useInitialFocusOnExtent';

import { SpinnerContainer } from 'components/drawerSections/DrawerSections';
import { replaceUtkastFeaturesInActiveSource } from 'components/map/Layers';
import { Colors } from 'components/map/Styles';
import { penCircleSvg } from 'components/map/icons';
import EnskiltBebyggelseobjektUtkastForm from 'components/utkast/utkastRegistration/EnskiltBebyggelseobjektUtkastForm';
import OmradeUtkastForm from 'components/utkast/utkastRegistration/OmradeUtkastForm';
import SammansattUtkastForm from 'components/utkast/utkastRegistration/SammansattUtkastForm';

import { PatchReservation, UpphovsmanTyp } from 'models';
import { HTTP_STATUS } from 'models/appModels/Request';

import { BebyggelseobjektgruppEnum, MapModeEnum } from 'enums';

import { reservationSlice, useReserveBebyggelseutkastMutation } from 'api/endpoints/reservationSlice';

export const UpphovsmanContext = createContext<{
  selectedUpphovsman: UpphovsmanTyp[];
  setSelectedUpphovsman: Dispatch<SetStateAction<UpphovsmanTyp[]>>;
}>({ selectedUpphovsman: [], setSelectedUpphovsman: () => [] });

const UtkastRegistration: FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  // Referens till reservationen vid anrop för att undvika att dessa körs ansync.
  const isReservationInProgress = useRef<{ inProgress: boolean; id: string }>();

  const { requestStatus: domanerRequestStatus, data: domaner } = useAppSelector(
    (state: RootState) => state.bebyggelseobjektDomaner
  );

  const {
    requestStatus: utkastRequestStatus,
    requestType: utkastRequestType,
    data: utkast
  } = useAppSelector((state: RootState) => state.utkast);

  const [selectedUpphovsman, setSelectedUpphovsman] = useState<UpphovsmanTyp[]>([]);

  const deletedOrPublishedRef = useRef<boolean>(false);

  const [patchReservation, { isLoading: patchingReservationIsLoading }] = useReserveBebyggelseutkastMutation({
    fixedCacheKey: 'utkast-reservation'
  });

  const { setDrawerMobileTemplate } = useDrawerMobileTemplate();

  useEffect(() => {
    if (utkast) {
      const upphovsman: UpphovsmanTyp[] = utkast.handelser.flatMap((d) => d.upphovsmanroller).map((d) => d.upphovsman);
      setSelectedUpphovsman(upphovsman);
    }
  }, [utkast, setSelectedUpphovsman]);

  useEffect(() => {
    if (utkast) {
      setDrawerMobileTemplate(
        <div className="tw-flex tw-flex-col tw-gap-2 tw-p-4">
          <h2 className="tw-heading-md tw-w-64 tw-truncate md:tw-w-full">{utkast.titel}</h2>
          <div className="tw-flex tw-items-center tw-justify-start">
            <img
              className="tw-mr-2"
              src={`data:image/svg+xml,${encodeURIComponent(penCircleSvg(Colors.GREEN))}`}
              alt="Ikon för utkast"
            />
            <p>Utkast {utkast.aktivitetref?.aktivitetsnamn && <>({utkast.aktivitetref?.aktivitetsnamn})</>}</p>
          </div>
        </div>
      );
    }
  }, [setDrawerMobileTemplate, utkast]);

  useEffect(() => {
    const initializeReservation = async () => {
      while (isReservationInProgress.current?.inProgress && isReservationInProgress.current?.id === id) {
        // Vänta på att pågående reservation ska slutföras
        await new Promise((resolve) => setTimeout(resolve, 100));
      }

      if (!id || isReservationInProgress.current?.inProgress) return;

      const reservation = async (reservation: PatchReservation) => {
        isReservationInProgress.current = { inProgress: true, id: reservation.id };

        const response = await patchReservation(reservation).unwrap();
        isReservationInProgress.current = { inProgress: false, id: reservation.id };

        if (reservation.reserve) {
          if (response.http_status_code === HTTP_STATUS.UNPROCESSABLE_ENTITY) {
            navigate(`/utkast/${id}`, { replace: true });
          } else {
            dispatch(setMapMode(MapModeEnum.UTKAST_REGISTRATION));
          }
        }
      };

      void reservation({ id, reserve: true });
    };

    void initializeReservation();

    // Cleanup
    return () => {
      const cleanup = async () => {
        while (isReservationInProgress.current?.inProgress && isReservationInProgress.current?.id === id) {
          // Vänta på att pågående reservation ska slutföras
          await new Promise((resolve) => setTimeout(resolve, 100));
        }

        // eslint-disable-next-line react-hooks/exhaustive-deps
        if (id && !deletedOrPublishedRef.current) {
          const reservation = { id, reserve: false };

          isReservationInProgress.current = { inProgress: true, id: reservation.id };
          await patchReservation(reservation);
          isReservationInProgress.current = { inProgress: false, id: reservation.id };
        }

        replaceUtkastFeaturesInActiveSource([]);
        dispatch(setMapMode(MapModeEnum.DEFAULT));
        dispatch(reservationSlice.util.resetApiState());
        dispatch(clearUtkast());
      };

      void cleanup();
      setDrawerMobileTemplate(undefined);
    };
  }, [id, dispatch, navigate, patchReservation, setDrawerMobileTemplate]);

  useInitialFocusOnExtent(useCallback(() => utkast && replaceUtkastFeaturesInActiveSource([utkast]), [utkast]));

  return (
    <SpinnerContainer
      showSpinner={
        (patchingReservationIsLoading || utkastRequestStatus === 'pending' || domanerRequestStatus === 'pending') &&
        !utkast
      }
    >
      <UpphovsmanContext.Provider value={{ selectedUpphovsman, setSelectedUpphovsman }}>
        {utkast && domaner && (
          <>
            {utkast.bebyggelseobjektgrupp === BebyggelseobjektgruppEnum.ENSKILT_BEBYGGELSEOBJEKT && (
              <EnskiltBebyggelseobjektUtkastForm
                utkastData={utkast}
                domaner={domaner}
                requestStatus={utkastRequestStatus}
                requestType={utkastRequestType}
                deletedOrPublishedRef={deletedOrPublishedRef}
              />
            )}
            {utkast.bebyggelseobjektgrupp === BebyggelseobjektgruppEnum.OMRADE && (
              <OmradeUtkastForm
                utkastData={utkast}
                domaner={domaner}
                requestStatus={utkastRequestStatus}
                requestType={utkastRequestType}
                deletedOrPublishedRef={deletedOrPublishedRef}
              />
            )}
            {utkast.bebyggelseobjektgrupp === BebyggelseobjektgruppEnum.SAMMANSATT && (
              <SammansattUtkastForm
                utkastData={utkast}
                domaner={domaner}
                requestStatus={utkastRequestStatus}
                requestType={utkastRequestType}
                deletedOrPublishedRef={deletedOrPublishedRef}
              />
            )}
          </>
        )}
      </UpphovsmanContext.Provider>
    </SpinnerContainer>
  );
};

export default UtkastRegistration;
